import { Spacing } from '@persona-id/design-tokens';
import EN_US from 'locales/translation.en-US.json';
import { compact, isEmpty } from 'lodash/fp';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import Permissioned from 'dashboard/components/Permissioned';
import IgnoreHitsModal from 'dashboard/components/ReportResult/IgnoreHitsModal';
import { MatchIconPositive } from 'dashboard/components/ReportResult/MatchIcons';
import {
  MatchDetails,
  MatchesAccordion,
  MatchHeading,
  MatchPositions,
} from 'dashboard/components/ReportResult/PoliticallyExposedPerson';
import { PoliticallyExposedPersonProps } from 'dashboard/components/ReportResult/PoliticallyExposedPersonPersona';
import { PoliticallyExposedPersonInputDetails } from 'dashboard/components/ReportResult/PoliticallyExposedPersonPersonaV2/PoliticallyExposedPersonInputDetails';
import PoliticallyExposedPersonV2EntityMatchDetails from 'dashboard/components/ReportResult/PoliticallyExposedPersonV2EntityMatchDetails';
import {
  PoliticallyExposedPersonIgnoreListEntry,
  PoliticallyExposedPersonMatch,
} from 'dashboard/models/report/politically-exposed-person';
import Card from 'design-system/components/Card';
import { TitleContainer } from 'design-system/components/Card/Header';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

// NOTE: Do NOT add a value of 0. This will be removed in the _.compact statement
// This is typically what folks do for All, so be very aware of this
export enum PEPFilterType {
  All = 1,
  Profile = 2,
  Portrait = 3,
  Positions = 4,
  Candidacies = 5,
  Relationships = 6,
  RelatedLinks = 7,
}

export type PEPFilters =
  | PEPFilterType.All
  | PEPFilterType.Profile
  | PEPFilterType.Portrait
  | PEPFilterType.Positions
  | PEPFilterType.Candidacies
  | PEPFilterType.Relationships
  | PEPFilterType.RelatedLinks;

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: 300px auto;
  overflow-x: auto;
`;

const SideNav = styled.div`
  grid-row: span 2;
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const SideNavWrapper = styled.section<{ border?: boolean }>`
  padding: ${Spacing[3]} ${Spacing[4]};

  & > ul > li {
    list-style-type: none;
  }

  ${({ border }) =>
    border &&
    css`
      border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
    `}
`;

const MainContent = styled.div`
  padding: ${Spacing[5]};
  grid-row: 2;
  grid-column: 2;
`;

export const gatherPepType = (pepType: string | undefined) => {
  switch (pepType) {
    case 'politically_exposed_person':
      return 'PEP';
    case 'relative_close_associate':
      return 'Close associate';
    case 'candidate':
      return 'Candidate';
    default:
      return 'Uncategorized';
  }
};

// Storing and typing the countries mapping
const COUNTRIES_MAPPING: Record<string, string> = EN_US.countries;

// Categorizes the match types and other pertinent matching information
const formatSubtitle = (identity: PoliticallyExposedPersonMatch) => {
  const pepType = gatherPepType(identity['pep-type']);

  const matchType = identity['match-types']?.includes('name_exact')
    ? 'Primary name'
    : identity['match-types']?.includes('name_fuzzy')
      ? 'Fuzzy Match'
      : 'Alias';
  const countryName =
    identity.locations && identity.locations.length > 0
      ? COUNTRIES_MAPPING[identity.locations[0]]
      : '';

  return `${pepType} · ${matchType} · ${countryName}`;
};

const IdMatchNavButton = styled.button<{ selected: boolean }>`
  padding: ${Spacing[2]};
  width: 100%;
  border-radius: ${({ theme }) => theme.element.borderRadius};
  ${paragraphMixins['md']};
  background-color: transparent;
  text-align: left;

  &:hover {
    cursor: pointer;
  }

  &:hover,
  &:active,
  &:focus {
    color: ${({ theme }) => theme.color.primary.brand};
    background-color: ${({ theme }) => theme.color.primary.brand100};
  }

  ${({ selected }) =>
    selected &&
    css`
      color: ${({ theme }) => theme.color.primary.brand};
      background-color: ${({ theme }) => theme.color.primary.brand100};
    `}
`;

const PoliticallyExposedPersonPersonaV2 = (props: PoliticallyExposedPersonProps) => {
  const { report } = props;

  const [selectedIdentityIndex, setSelectedIdentityIndex] = useState<number>(0);
  const [ignoreHitsModal, { enable: showIgnoreHitsModal, disable: hideIgnoreHitsModal }] =
    useToggle(false);
  const { result: entityLists, 'ignore-list': ignoreList = [] } = report.attributes;

  // Get our identity matches + dismissed identities (Send dismissals to the back)
  const ignoredEntities =
    ignoreList?.map((entry: PoliticallyExposedPersonIgnoreListEntry) => entry.match) ?? [];

  const identityRecords = [...entityLists, ...ignoredEntities];

  if (!isEmpty(identityRecords)) {
    const selectedIdentity = identityRecords[selectedIdentityIndex];
    return (
      <Card>
        <Container>
          <SideNav>
            {report.attributes?.query &&
              compact(Object.values(report.attributes.query)).length > 0 && (
                <SideNavWrapper border={!isEmpty(identityRecords)}>
                  <Paragraph size='lg' weight='bold' color='primary' as='h4'>
                    Input
                  </Paragraph>
                  <Spacer height={2} />
                  <PoliticallyExposedPersonInputDetails input={report.attributes.query} />
                  <Spacer height={2} />
                </SideNavWrapper>
              )}
            {!isEmpty(identityRecords) && (
              <SideNavWrapper>
                <TitleContainer>
                  <Paragraph size='lg' weight='bold' color='primary' as='h4'>
                    Identity matches
                  </Paragraph>{' '}
                  {entityLists.length > 0 && (
                    <Permissioned permission={Permission.ReportIgnoreHitsWrite}>
                      <ContextMenu
                        variant='inverse'
                        items={[
                          {
                            id: 'reports-ignore-hits-modal__dismiss-hits-button',
                            onClick: showIgnoreHitsModal,
                            hidden: false,
                            label: 'Dismiss all matches',
                          },
                        ]}
                        renderItem={(item) => (
                          <ContextMenu.Item key={item.id} id={item.id} onClick={item.onClick}>
                            {item.label}
                          </ContextMenu.Item>
                        )}
                      />
                      <IgnoreHitsModal
                        report={report}
                        ignoreType='all'
                        ignoreHitsModalOpen={ignoreHitsModal}
                        hideIgnoreHitsModal={hideIgnoreHitsModal}
                      />
                    </Permissioned>
                  )}
                </TitleContainer>
                <Spacer height={2} />
                <ul>
                  {identityRecords?.map((identityMatch, index) => {
                    return (
                      <li key={`identity-record-${index}`}>
                        <IdMatchNavButton
                          id={`pep-report__select-identity-${index}`}
                          selected={selectedIdentityIndex === index}
                          onClick={() => {
                            setSelectedIdentityIndex(index);
                          }}
                          data-chromatic='ignore'
                          tabIndex={0}
                        >
                          {identityMatch.name}{' '}
                          {ignoredEntities.includes(identityMatch) && <Sticker>Dismissed</Sticker>}
                          <Paragraph size='sm'>{formatSubtitle(identityRecords[index])}</Paragraph>
                        </IdMatchNavButton>
                      </li>
                    );
                  })}
                </ul>
              </SideNavWrapper>
            )}
          </SideNav>
          <MainContent>
            {
              // A dismissed identity could be from CA. Thus, we'll want to render it in the legacy format
              // in the event that we have some overlapping data results
              selectedIdentity['data-source'] === 'comply_advantage' ? (
                <>
                  <MatchesAccordion
                    key={`${selectedIdentity.name}-${selectedIdentity.birthdates?.join('-')}`}
                    menuItem={true}
                    menuItemBorder={false}
                    menuItemHover={false}
                    caretSize='md'
                    padded={false}
                    defaultExpanded={true}
                    titleContainerHeight='auto'
                    title={<MatchHeading match={selectedIdentity} />}
                  >
                    <MatchDetails>
                      <MatchPositions match={selectedIdentity} />
                    </MatchDetails>
                    <Spacer height={2} />
                  </MatchesAccordion>
                  <Spacer height={1} />
                </>
              ) : (
                <PoliticallyExposedPersonV2EntityMatchDetails
                  report={report}
                  entity={selectedIdentity}
                  isIgnoredEntity={ignoredEntities?.includes(selectedIdentity)}
                />
              )
            }
          </MainContent>
        </Container>
      </Card>
    );
  } else {
    // Null state
    return (
      <Card>
        <Container>
          <SideNav>
            {report.attributes?.query &&
              compact(Object.values(report.attributes.query)).length > 0 && (
                <SideNavWrapper border={!isEmpty(identityRecords)}>
                  <Paragraph size='lg' weight='bold' color='primary' as='h4'>
                    Input
                  </Paragraph>
                  <Spacer height={2} />
                  <PoliticallyExposedPersonInputDetails input={report.attributes.query} />
                  <Spacer height={2} />
                </SideNavWrapper>
              )}
          </SideNav>
          <MainContent>
            <div>
              <Paragraph size='lg' weight='bold' color='primary' as='h4'>
                Results
              </Paragraph>
              <Spacer height={2} />
              <MatchIconPositive label='No matches found.' />
            </div>
          </MainContent>
        </Container>
      </Card>
    );
  }
};
export default PoliticallyExposedPersonPersonaV2;
